import React from "react";
import { AppLink } from "../shared";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import { canOpenFirmDetail } from "../../utils/firmUtils";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaIndustry,
  FaWarehouse,
  FaStore,
  FaUserTie,
  FaCoins,
  FaBuilding,
  FaShieldAlt,
  FaExternalLinkAlt,
  FaChevronRight,
} from "react-icons/fa";
import coffeeBean from "../../Assets/icons/coffee_bean.png";
import teaLeaf from "../../Assets/icons/tea_leaf.png";
import FirmBusyBadge from "./FirmBusyBadge";
import { FIRM_TYPE_STYLES, getFirmTypeKey, getFirmTypeStyle } from "../../utils/firmTypeStyles";

// Tip etiketinin ikonu (#215)
const TYPE_ICONS = {
  storage: FaIndustry,
  dealer: FaStore,
  mainDealer: FaStore,
  customer: FaUserTie,
};

const PRODUCT_ICONS = {
  Çay: teaLeaf,
  "Türk Kahvesi": coffeeBean,
  "Filtre Kahve": coffeeBean,
  Espresso: coffeeBean,
};

// Kotalar sütununda yalnızca bu ürünler gösterilir (talep: diğerleri gizlenir).
const ALLOWED_QUOTA_PRODUCTS = ["Espresso", "Çay", "Türk Kahvesi", "Filtre Kahve"];
const visibleQuotas = (quotaArray) =>
  Array.isArray(quotaArray)
    ? quotaArray.filter((q) => ALLOWED_QUOTA_PRODUCTS.includes(q?.name))
    : [];

const FirmCard = ({
  firm,
  canNavigate,
  isMobileView = false,
  // Firmada işlem sürüyor (kilit tutuluyor) — "İşlem sürüyor" rozeti (#303).
  busy = false,
  // Satır sonu işlem hücresi (ör. Müşterilerim'de Kota Ver / Al). Sütun yalnız
  // showActionsColumn ile çizilir; liste başlığı da buna göre kurulur (FirmList).
  actions = null,
  showActionsColumn = false,
  // AĞAÇ GÖRÜNÜMÜ (#215, Genel Firmalar): { depth, hasChildren, expanded,
  // onToggle, descendantCount }. Verilmezse satır eskisi gibi düz çizilir.
  tree = null,
  // Ağaçta "Ana Firma" / "Bağlı Bayiler" sütunları gereksiz — hiyerarşi
  // satırların kendisinde görünür.
  hideRelationColumns = false,
  // Aramada eşleşmeyen ama bulunan firmanın üst zincirinde olan firma:
  // yalnız ad + tip, soluk.
  stub = false,
}) => {
  const { auth } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermission();
  // Tıklanabilirlik FİRMA BAZINDA: detay sayfasının sekme hesabıyla birebir
  // aynı. Sayfa seviyesindeki canNavigate (tüm yetkilerin birleşimi) dış kapı
  // olarak kalır; bu kontrol o firmaya gerçekten girilebiliyor mu diye bakar —
  // yoksa kart tıklanıp 403'e düşülüyordu.
  const canOpen =
    canNavigate &&
    canOpenFirmDetail(firm, {
      role: auth?.role === "dealer" ? "dealer" : undefined,
      hasPermission,
      hasAnyPermission,
    });
  // Firma tipi renkleri tek kaynaktan (#215) — Depo mor, Bayi mavi,
  // Müşteri teal, Ana Bayi amber.
  const typeKey =
    firm.dealerRelation === "anaBayi"
      ? "mainDealer"
      : firm.dealerRelation === "bayi"
        ? "dealer"
        : firm.dealerRelation === "musteri"
          ? "customer"
          : getFirmTypeKey(firm);
  const typeStyle = FIRM_TYPE_STYLES[typeKey];
  const TypeIcon = TYPE_ICONS[typeKey];

  // Firma türüne göre ikon (renk tip ailesinden)
  const getFirmIcon = (size = "w-8 h-8") => {
    const color = getFirmTypeStyle(firm).icon;
    if (firm.firmType === "storage") return <FaWarehouse className={`${color} ${size}`} />;
    if (firm.firmType === "dealer") return <FaStore className={`${color} ${size}`} />;
    if (firm.isCorporate) return <FaIndustry className={`${color} ${size}`} />;
    return <FaUserTie className={`${color} ${size}`} />;
  };

  const typeBadge = { label: typeStyle.label, color: typeStyle.chip };

  // Açık grup tonu (#215): kök + alt firmaları kökün tip renginde, solda şerit.
  const groupStyle = tree?.group ? FIRM_TYPE_STYLES[tree.group.typeKey] : null;
  // Zemin tonu satıra; sol şerit masaüstünde ilk hücredeki mutlak çubukla
  // (<tr> kenarlığı tablo kenarlık modeline göre tutarsız çiziliyordu),
  // mobilde kartın sol kenarlığıyla.
  const groupBg = groupStyle
    ? tree.group.role === "head" ? groupStyle.groupHead : groupStyle.groupBody
    : "";
  const groupClass = groupStyle ? `${groupBg} border-l-4 ${groupStyle.groupRail}` : "";
  const groupRail = groupStyle ? (
    <span aria-hidden="true" className={`absolute left-0 top-0 bottom-0 w-1 ${groupStyle.groupRailBar}`} />
  ) : null;

  // Ağaç: girinti + aç/kapa oku (çocuğu varsa) ya da ↳ (alt seviyede yaprak).
  const depth = Number(tree?.depth || 0);
  const treeControl = tree ? (
    <div className="flex items-center shrink-0" style={{ paddingLeft: depth * 20 }}>
      {tree.hasChildren && !tree.onToggle ? (
        // Arama/filtrede ağaç hep açık — ok yalnız gösterge.
        <span className="w-6 h-6 flex items-center justify-center text-gray-400">
          <FaChevronRight className="w-3 h-3 rotate-90" />
        </span>
      ) : tree.hasChildren ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            tree.onToggle?.();
          }}
          className="relative z-10 w-6 h-6 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
          aria-label={tree.expanded ? "Alt firmaları gizle" : "Alt firmaları göster"}
          aria-expanded={tree.expanded}
          title={tree.expanded ? "Alt firmaları gizle" : `${tree.descendantCount} alt firmayı göster`}
        >
          <FaChevronRight
            className={`w-3 h-3 transition-transform ${tree.expanded ? "rotate-90" : ""}`}
          />
        </button>
      ) : (
        <span className="w-6 h-6 flex items-center justify-center text-gray-300 text-sm select-none">
          {depth > 0 ? "↳" : ""}
        </span>
      )}
    </div>
  ) : null;
  const childCountTag =
    tree?.hasChildren && !tree.expanded ? (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${typeStyle.chip}`}>
        {tree.descendantCount} alt firma
      </span>
    ) : null;

  // Zincir satırı (stub): sade ve soluk — kota/iletişim bilgisi yok.
  if (stub) {
    const nameNode = (
      <span className="flex items-center gap-2 min-w-0">
        <span className="shrink-0">{getFirmIcon("w-4 h-4")}</span>
        <span className="text-sm font-semibold text-gray-700 truncate">{firm.firmName}</span>
        {firm.firmType === "dealer" && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${FIRM_TYPE_STYLES.dealer.chip}`}>
            <FaStore className="w-2.5 h-2.5 shrink-0" /> Bayi
          </span>
        )}
      </span>
    );
    const linked = canOpen ? (
      <AppLink
        to={`/Anasayfa/Firma/${firm._id}`}
        state={{ firm, fromPage: "Firma" }}
        className="min-w-0 hover:underline underline-offset-2"
        aria-label={`${firm.firmName} firmasına git`}
        title="Firmaya git"
      >
        {nameNode}
      </AppLink>
    ) : (
      nameNode
    );
    if (isMobileView) {
      return (
        <div className={`flex items-center gap-1 px-3.5 py-2 border-b border-gray-100 opacity-70 ${groupClass || "bg-gray-50/60"}`}>
          {treeControl}
          {linked}
        </div>
      );
    }
    return (
      <tr className={`border-b border-gray-100 opacity-70 ${groupBg || "bg-gray-50/60"}`}>
        <td className="relative px-4 py-2" colSpan={(hideRelationColumns ? 3 : 5) + (showActionsColumn ? 1 : 0)}>
          {groupRail}
          <div className="flex items-center gap-1 min-w-0">
            {treeControl}
            {linked}
          </div>
        </td>
      </tr>
    );
  }

  // Tür etiketleri (eski TÜR sütunundan Bayser altına taşındı) — tek kaynak renkleri.
  const renderTypeTags = () => (
    <div className="flex flex-wrap gap-1">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${typeStyle.chip}`}>
        <TypeIcon className="w-3 h-3 shrink-0" /> {typeStyle.label}
      </span>
      {firm.panelAdmin && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium whitespace-nowrap">
          <FaShieldAlt className="w-3 h-3 shrink-0" /> Panel Admin
        </span>
      )}
    </div>
  );

  // Mobile Card View
  if (isMobileView) {
    return (
      <div
        className={`relative px-3.5 py-3 transition-colors border-b border-gray-200 ${groupClass || "bg-white"} ${canOpen ? "active:bg-gray-50 cursor-pointer" : ""}`}
      >
        {canOpen && (
          <AppLink
            to={`/Anasayfa/Firma/${firm._id}`}
            state={{ firm, fromPage: "Firma" }}
            className="absolute inset-0 z-0"
            aria-label={`${firm.firmName} firmasına git`}
          />
        )}
        {/* Üst: Firma adı + Durum */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            {treeControl}
            <div className={`p-2 rounded-lg shrink-0 border ${getFirmTypeStyle(firm).iconBox}`}>
              {getFirmIcon("w-6 h-6")}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate">{firm.firmName}</div>
              <div className="text-xs text-gray-500">Bayser: {firm.bayserNo || "-"}</div>
              {childCountTag && <div className="mt-1">{childCountTag}</div>}
              {busy && <FirmBusyBadge className="mt-1" />}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${typeBadge.color}`}>
              {typeBadge.label}
            </span>
            <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-md ${firm.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {firm.isActive ? "Aktif" : "Pasif"}
            </span>
          </div>
        </div>

        {/* Orta: İletişim */}
        <div className="flex items-center gap-3 mb-2 text-xs text-gray-600">
          {firm.tel && (
            <span className="flex items-center gap-1.5 truncate">
              <FaPhone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              {firm.tel}
            </span>
          )}
          {firm.address?.city && (
            <>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1.5 truncate">
                <FaMapMarkerAlt className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                {firm.address.town}/{firm.address.city}
              </span>
            </>
          )}
        </div>

        {/* Alt: Kotalar + Türler - Pasif firmalarda kota gösterilmez */}
        <div className="flex flex-wrap gap-1.5">
          {firm.isCorporate && (
            <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded text-xs font-medium">Kurumsal</span>
          )}
          {/* KOTA HESABI MASAUSTUYLA AYNI OLMAK ZORUNDA (2026-08-25 duzeltmesi).
              Mobil kart yalnizca `firmQuota` yaziyordu; masaustu satiri ise
              `deviceQuota + firmQuota` topluyor (asagidaki Kotalar sutunu).
              Sonucta ayni firma icin iki gorunumde FARKLI sayi cikiyordu ve
              mobildeki eksik olan taraftı — cihazlara dagitilmis kota hic
              gorunmuyordu. Binlik ayraci da masaustuyle ayni bicimde. */}
          {firm.isActive && firm.firmType !== "storage" && visibleQuotas(firm.quotaArray).length > 0 && (
            visibleQuotas(firm.quotaArray).map((item, idx) => (
              <span key={idx} className="inline-flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-100 rounded-md px-2 py-1 font-medium text-gray-700">
                <img src={PRODUCT_ICONS[item.name] || coffeeBean} alt="" className="w-3.5 h-3.5 object-contain" />
                {item.name}: <span className="font-bold text-gray-900">
                  {((item.deviceQuota ?? 0) + (item.firmQuota ?? 0)).toLocaleString("tr-TR")}
                </span>
              </span>
            ))
          )}
        </div>
        {actions && <div className="relative z-10 mt-2">{actions}</div>}
      </div>
    );
  }

  // Desktop Table Row View
  return (
    <tr
      className={`transition-colors duration-200 border-b border-gray-100 ${
        groupBg || "bg-white even:bg-gray-50/70 hover:bg-gray-100/70"
      }`}
    >
      {/* Firma Adı ve İkon — ikon + isim + bayser tek link; herhangi birine
          hover'da hepsi link görünümüne geçer (cihaz listesi deseni).
          Eski "Git" kutusu kaldırıldı; Aktif/Pasif rozeti isim bloğunun altında */}
      <td className={`relative px-4 py-3 ${hideRelationColumns ? "w-[46%]" : "w-[30%]"}`}>
        {groupRail}
        <div className="flex items-start gap-1 min-w-0">
        {treeControl}
        <div className="flex flex-col gap-1.5 min-w-0">
          {(() => {
            const inner = (
              <>
                <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center border transition-colors ${getFirmTypeStyle(firm).iconBox} ${getFirmTypeStyle(firm).iconBoxHover}`}>
                  {getFirmIcon("w-5 h-5 transition-colors")}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-gray-900 truncate transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                    {firm.firmName}
                  </div>
                  <div className="text-xs text-gray-400 truncate transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                    Bayser: {firm.bayserNo || "-"}
                  </div>
                </div>
              </>
            );
            return canOpen ? (
              <AppLink
                to={`/Anasayfa/Firma/${firm._id}`}
                state={{ firm, fromPage: "Firma" }}
                className="group flex items-center gap-2.5 min-w-0 w-fit max-w-full"
                aria-label={`${firm.firmName} firmasına git`}
                title="Firmaya git"
              >
                {inner}
              </AppLink>
            ) : (
              <div className="flex items-center gap-2.5 min-w-0">{inner}</div>
            );
          })()}
          {/* Aktiflik + firma tipi rozetleri aynı satırda */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                firm.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${firm.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
              {firm.isActive ? "Aktif" : "Pasif"}
            </span>
            {renderTypeTags()}
            {childCountTag}
            {busy && <FirmBusyBadge />}
          </div>
        </div>
        </div>
      </td>

      {/* Kotalar - Sadece aktif ve depo olmayan firmalarda gösterilir */}
      <td className="px-4 py-3 w-[240px] max-w-[240px]">
        {firm.firmType === "storage" ? (
          <span className="text-xs text-gray-400 italic">Depo firmalarında kota gösterimi olmaz</span>
        ) : !firm.isActive ? (
          <span className="text-xs text-gray-400 italic">Pasif firmada kota gösterimi olmaz</span>
        ) : (
          /* Cihaz listesi deseni: ürün başına satır — ikon + isim + kota
             (cihaz + firma kotası tek toplam) */
          visibleQuotas(firm.quotaArray).length > 0 ? (
            <div className="flex flex-col gap-1 overflow-hidden">
              {visibleQuotas(firm.quotaArray).map((item, index) => (
                <span
                  key={`${item.name}-${index}`}
                  className="inline-flex items-center gap-2 text-sm whitespace-nowrap min-w-0"
                >
                  <img
                    src={PRODUCT_ICONS[item.name] || coffeeBean}
                    alt={item.name || "Ürün"}
                    className="w-3.5 h-3.5 object-contain shrink-0"
                  />
                  <span className="text-gray-600 truncate">{item.name || "-"}</span>
                  <span className="font-bold text-indigo-600 tabular-nums shrink-0">
                    {((item.deviceQuota ?? 0) + (item.firmQuota ?? 0)).toLocaleString("tr-TR")}
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">Kota tanımlanmamış</span>
          )
        )}
      </td>


      {/* Ana Firma */}
      {!hideRelationColumns && (
      <td className="px-4 py-3 w-[14%]">
        {firm.mainFirm ? (
          canOpen ? (
            <AppLink
              to={`/Anasayfa/Firma/${firm.mainFirm._id}`}
              state={{ firm: firm.mainFirm, fromPage: "Firma" }}
              className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
              aria-label={`${firm.mainFirm.firmName} firmasına git`}
              title="Firmaya git"
            >
              <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 transition-colors group-hover:bg-indigo-100 group-hover:border-indigo-300">
                <FaBuilding className="w-4 h-4 text-indigo-500 transition-colors group-hover:text-indigo-700" />
              </div>
              <span className="text-xs font-medium text-gray-800 break-words min-w-0 transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                {firm.mainFirm.firmName}
              </span>
            </AppLink>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100">
                <FaBuilding className="w-4 h-4 text-indigo-500" />
              </div>
              <span className="text-xs font-medium text-gray-800 break-words min-w-0">{firm.mainFirm.firmName}</span>
            </div>
          )
        ) : (
          <span className="text-xs text-gray-400 italic">Ana firması yok</span>
        )}
      </td>
      )}

      {/* Bağlı Bayiler */}
      {!hideRelationColumns && (
      <td className="px-4 py-3 w-[14%]">
        {(() => {
          const dealers = Array.isArray(firm.dealerFirm)
            ? firm.dealerFirm.filter((d) => d && d._id)
            : [];
          return dealers.length > 0 ? (
            <div className="flex flex-col gap-1.5 min-w-0">
              {dealers.map((dealer) =>
                canOpen ? (
                  <AppLink
                    key={dealer._id}
                    to={`/Anasayfa/Firma/${dealer._id}`}
                    state={{ firm: dealer, fromPage: "Firma" }}
                    className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
                    aria-label={`${dealer.firmName || "Bayi"} bayisine git`}
                    title="Bayiye git"
                  >
                    <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 transition-colors group-hover:bg-blue-100 group-hover:border-blue-300">
                      <FaStore className="w-4 h-4 text-blue-500 transition-colors group-hover:text-blue-700" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-semibold text-gray-800 break-words leading-snug transition-colors group-hover:text-blue-700 group-hover:underline underline-offset-2 decoration-blue-400">
                        {dealer.firmName || "—"}
                      </span>
                      {dealer.bayserNo && (
                        <span className="text-[11px] font-medium text-gray-400 truncate transition-colors group-hover:text-blue-500 group-hover:underline underline-offset-2 decoration-blue-300">
                          Bayser: {dealer.bayserNo}
                        </span>
                      )}
                    </div>
                  </AppLink>
                ) : (
                  <div key={dealer._id} className="flex items-center gap-2 min-w-0">
                    <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-blue-50 border border-blue-100">
                      <FaStore className="w-4 h-4 text-blue-500" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-semibold text-gray-800 break-words leading-snug">
                        {dealer.firmName || "—"}
                      </span>
                      {dealer.bayserNo && (
                        <span className="text-[11px] font-medium text-gray-400 truncate">
                          Bayser: {dealer.bayserNo}
                        </span>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">Bağlı bayi yok</span>
          );
        })()}
      </td>
      )}

      {/* İletişim */}
      <td className={`px-4 py-3 ${hideRelationColumns ? "w-[30%]" : "w-[24%]"} min-w-0`}>
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <FaPhone className="text-gray-400 w-3.5 h-3.5 shrink-0" />
            <span className="text-xs text-gray-700 truncate" title={firm.tel}>{firm.tel || "—"}</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <FaEnvelope className="text-gray-400 w-3.5 h-3.5 shrink-0" />
            <span className="text-xs text-gray-700 truncate" title={firm.email}>{firm.email || "—"}</span>
          </div>
        </div>
      </td>
      {showActionsColumn && (
        <td className="px-4 py-3 w-[150px] text-right">{actions}</td>
      )}
    </tr>
  );
};

export default FirmCard;
